import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { environment } from '../../config/environment'

interface Item {
  id: string
  name: string
  type: string
  rarity: string
  quantity: number
  value: number
}

interface InventoryPanelProps {
  childId?: string
  onItemUse?: (itemId: string) => void
}

const rarityColors: Record<string, string> = {
  common: 'bg-gray-100 border-gray-300',
  uncommon: 'bg-green-100 border-green-300',
  rare: 'bg-blue-100 border-blue-300',
  epic: 'bg-purple-100 border-purple-300',
  legendary: 'bg-yellow-100 border-yellow-300',
}

const typeIcons: Record<string, string> = {
  food: '🍎',
  toy: '🎮',
  book: '📚',
  material: '🔧',
  equipment: '⚔️',
}

export function InventoryPanel({ childId, onItemUse }: InventoryPanelProps): React.ReactElement {
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState<Item | null>(null)

  useEffect(() => {
    fetchInventory()
  }, [])

  const fetchInventory = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      const response = await axios.get(`${environment.API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setItems(response.data)
    } catch (error) {
      console.error('Failed to fetch inventory', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUseItem = async (itemId: string) => {
    if (!childId) {
      alert('Please select a child first')
      return
    }

    try {
      const token = localStorage.getItem('authToken')
      await axios.post(
        `${environment.API_URL}/inventory/use`,
        { itemId, childId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      onItemUse?.(itemId)
      fetchInventory()
    } catch (error) {
      console.error('Failed to use item', error)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 max-w-md">
      <h2 className="text-xl font-bold mb-4 text-gray-800">Inventory</h2>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-center text-gray-500">No items yet</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((item) => (
            <div
              key={item.id}
              className={`border-2 rounded-lg p-3 cursor-pointer transition ${
                selectedItem?.id === item.id ? 'border-blue-500 bg-blue-50' : rarityColors[item.rarity]
              }`}
              onClick={() => setSelectedItem(item)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{typeIcons[item.type] || '📦'}</span>
                  <div>
                    <p className="font-semibold text-gray-800">{item.name}</p>
                    <p className="text-sm text-gray-600 capitalize">{item.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">x{item.quantity}</p>
                  <p className="text-sm text-gray-500">{item.value}💰</p>
                </div>
              </div>

              {selectedItem?.id === item.id && childId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleUseItem(item.id)
                  }}
                  className="w-full mt-2 bg-blue-500 text-white py-1 rounded font-semibold hover:bg-blue-600 text-sm"
                >
                  Use Item
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t">
        <button
          onClick={fetchInventory}
          className="w-full bg-gray-500 text-white py-2 rounded font-semibold hover:bg-gray-600"
        >
          Refresh
        </button>
      </div>
    </div>
  )
}
